import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PrimaryButton, DefaultButton, TextField, Stack, MessageBar, MessageBarType } from "@fluentui/react";
import { validateUserId, validatePassword } from "../admin/utils";
import styles from "./ChangePassword.module.css";
import { ConfirmDialog } from "../admin/components";
import { APIResult } from "../../api/admin/models_admin";

const changePasswordAPI = async (mykey: string, userid: string, oldPassword: string, newPassword: string, rePassword: string) => {
    const formData = new FormData();
    formData.append('userid', userid);
    formData.append('oldpassword', oldPassword);
    formData.append('newpassword', newPassword);
    formData.append('repassword', rePassword);
    const response = await fetch(`/api/change_password/${mykey}`, {
        method: 'POST',
        body: formData
    });
    const result: APIResult = await response.json();
    return result
}

export const ChangePassword = () => {
    const { mykey } = useParams<{ mykey: string }>();
    const navigate = useNavigate();
    
    const [userid, setUserid] = useState("");
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [rePassword, setRePassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState<string | null>(null)

    const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage("");
        
        if (isLoading) return;
        if (!mykey) return;

        // バリデーション
        if (!userid) {
            setErrorMessage("ユーザーIDを入力してください。");
            return;
        }
        if (!validateUserId(userid)) {
            setErrorMessage("使用できる文字は、半角英数字（大文字・小文字）と @ - _ . のみです。");
            return;
        }

        if (!newPassword) {
            setErrorMessage("新しいパスワードを入力してください。");
            return;
        }
        if (!validatePassword(newPassword)) {
            setErrorMessage("パスワードに使用できる文字は半角英数字記号のみです。");
            return;
        }

        if (!rePassword) {
            setErrorMessage("新しいパスワード(確認)を入力してください。");
            return;
        }
        if (!validatePassword(rePassword)) {
            setErrorMessage("パスワードに使用できる文字は半角英数字記号のみです。");
            return;
        }

        if (newPassword !== rePassword) {
            setErrorMessage("新しいパスワードとパスワード(確認)が一致していません。");
            return;
        }

        setIsLoading(true);

        try {
            const result = await changePasswordAPI(mykey, userid, oldPassword, newPassword, rePassword)

            if (result.success) {
                setSuccessMessage(result.message || 'パスワードを変更しました。')
            } else {
                setErrorMessage(result.message || 'パスワードの変更に失敗しました。');
            }
        } catch (error) {
            setErrorMessage('パスワード変更処理中にエラーが発生しました。');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate(`/login/${mykey}`);
    };

    return (
        <div className={styles.changePasswordPage}>
            <h1 className={styles.pageTitle}>パスワード変更</h1>
            
            <div className={styles.container}>
                {errorMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={true}
                        onDismiss={() => setErrorMessage("")}
                        dismissButtonAriaLabel="閉じる"
                    >
                        {errorMessage}
                    </MessageBar>
                )}

                <form onSubmit={handleChangePassword}>
                    <div className={styles.formContainer}>
                        <Stack tokens={{ childrenGap: 20 }}>
                            <TextField
                                label="ユーザーID"
                                value={userid}
                                onChange={(_, newValue) => setUserid(newValue || "")}
                                required
                                maxLength={100}
                                placeholder="半角英数字と @ - _ . のみ"
                                autoComplete="username"
                            />
                            <TextField
                                label="現在のパスワード"
                                type="password"
                                value={oldPassword}
                                onChange={(_, newValue) => setOldPassword(newValue || "")}
                                maxLength={100}
                                required
                                canRevealPassword
                                autoComplete="current-password"
                            />
                            <TextField
                                label="新しいパスワード"
                                type="password"
                                value={newPassword}
                                onChange={(_, newValue) => setNewPassword(newValue || "")}
                                required
                                maxLength={100}
                                placeholder="半角英数字記号のみ"
                                canRevealPassword
                            />
                            <TextField
                                label="新しいパスワード(確認)"
                                type="password"
                                value={rePassword}
                                onChange={(_, newValue) => setRePassword(newValue || "")}
                                required
                                maxLength={100}
                                placeholder="半角英数字記号のみ"
                                canRevealPassword
                            />
                        </Stack>
                    </div>

                    <Stack className={styles.buttonContainer} tokens={{childrenGap: "15px"}}>
                        <PrimaryButton 
                            type="submit" 
                            className={styles.changeButton}
                            disabled={isLoading}
                        >
                            変更
                        </PrimaryButton>
                        <DefaultButton 
                            className={styles.changeButton}
                            onClick={handleBackToLogin}
                            disabled={isLoading}
                        >
                            ログインページに戻る
                        </DefaultButton>
                    </Stack>
                </form>
            </div>
            <ConfirmDialog
                isOpen={successMessage !== null}
                onDismiss={handleBackToLogin}
                title="確認"
                content={successMessage ?? ""}
            />

        </div>
    );
}